import { useState, useEffect } from "react";
import { Link, useLocation } from "@tanstack/react-router";
import { useAppStore } from "@/lib/store";
import { Menu, X, ArrowUp } from "lucide-react";
import { AvatarDisplay } from "@/components/AvatarDisplay";

interface NavLink {
  to: string;
  label: string;
}

export function Header() {
  const location = useLocation();
  const { isAuthenticated, isGuest, user } = useAppStore();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  const navLinks: NavLink[] = [
    { to: "/", label: "HOME" },
    { to: "/app-tests", label: "APP TESTS" },
    { to: "/webapp-tests", label: "WEBAPP TESTS" },
    { to: "/testimonials", label: "REVIEWS" },
    { to: "/wallet", label: "WALLET" },
    ...(isAuthenticated ? [{ to: "/profile", label: "PROFILE" }] : []),
  ];

  useEffect(() => {
    const body = document.body;
    body.style.overflow = mobileOpen ? "hidden" : "auto";
    body.classList.toggle("mobile-menu-open", mobileOpen);

    return () => {
      body.style.overflow = "auto";
      body.classList.remove("mobile-menu-open");
    };
  }, [mobileOpen]);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const isActive = (href: string) => {
    if (href === "/") return location.pathname === "/";
    return location.pathname.startsWith(href);
  };

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-blue-200 shadow-lg">
        <div
          className="absolute inset-0 bg-linear-to-r from-blue-700 via-blue-600 to-indigo-700"
          style={{ opacity: 0.95 }}
        />
        <div className="relative mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6">
          <Link to="/" className="flex items-center gap-2.5 hover:opacity-90 transition-opacity">
            <div className="flex h-10 w-10 sm:h-11 sm:w-11 items-center justify-center rounded-xl bg-white/20 backdrop-blur-sm shadow-lg ring-2 ring-white/30">
              <img
                src="/patataskslogo.png"
                alt="PataTasks Logo"
                className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-contain"
              />
            </div>
            <div className="block sm:block">
              <span className="text-sm sm:text-lg font-bold text-white block leading-tight">
                PataTasks
              </span>
              <span className="text-[8px] sm:text-[9px] font-semibold text-white uppercase tracking-wider block leading-tight">
                App & Web Testing
              </span>
            </div>
          </Link>

          <div className="flex items-center gap-2 md:gap-4">
            <nav className="hidden items-center gap-2 md:gap-3 md:flex">
              {navLinks.map((link) => {
                const active = isActive(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`relative flex items-center rounded-lg px-4 py-2 text-sm font-extrabold whitespace-nowrap min-h-[44px] hover:decoration-green-500 hover:underline hover:decoration-4 hover:underline-offset-6 ${
                      active
                        ? "text-white underline decoration-4 decoration-green-500 underline-offset-6"
                        : "text-blue-50 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </nav>

            <div className="hidden items-center gap-2 md:flex">
              {isAuthenticated || isGuest ? (
                user && <AvatarDisplay />
              ) : (
                <Link
                  to="/signup"
                  className="rounded-lg px-4 py-2 text-sm font-bold text-white transition-all duration-200 bg-green-600 hover:bg-blue-600 min-h-[44px] inline-flex items-center justify-center"
                >
                  Sign Up
                </Link>
              )}
            </div>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="relative z-110 flex flex-col items-center justify-center gap-1.5 rounded-lg p-2 md:hidden text-white hover:bg-white/10 transition-colors min-h-[44px] min-w-[44px]"
            aria-label="Toggle menu"
          >
            {mobileOpen ? (
              <X className="h-6 w-6" />
            ) : (
              <>
                <span className="w-6 h-0.5 bg-white transition-transform" />
                <span className="w-6 h-0.5 bg-white transition-transform" />
                <span className="w-6 h-0.5 bg-white transition-transform" />
              </>
            )}
          </button>
        </div>
      </header>

      {mobileOpen && (
        <div
          className="fixed inset-0 z-90 bg-black/50 backdrop-blur-md md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className={`fixed inset-y-0 right-0 z-100 w-72 max-w-[92vw] shadow-2xl transform transition-transform duration-300 ease-out md:hidden ${
          mobileOpen ? "translate-x-0" : "translate-x-full"
        }`}
        style={{ background: "linear-gradient(to bottom, #1e3a8a, #1e40af, #2563eb)" }}
      >
        <div className="flex flex-col h-full">
          <div className="flex items-center justify-between border-b border-white/10 p-4">
            <span className="font-bold text-white">Navigation</span>
            <button
              onClick={() => setMobileOpen(false)}
              className="p-2 hover:bg-white/10 rounded-lg transition-colors min-h-[44px] min-w-[44px]"
            >
              <X className="h-5 w-5 text-white" />
            </button>
          </div>

          {(isAuthenticated || isGuest) && user && (
            <div className="flex items-center gap-3 border-b border-white/10 px-4 py-3">
              <AvatarDisplay />
              <div className="flex flex-col">
                <span className="text-sm font-semibold text-white">{user.fullName}</span>
                <span className="text-xs text-blue-200">Tap for menu</span>
              </div>
            </div>
          )}

          <nav className="min-h-0 flex-1 overflow-y-auto p-4 pb-6">
            <div className="space-y-1">
              {navLinks.map((link) => {
                const active = isActive(link.to);
                return (
                  <Link
                    key={link.to}
                    to={link.to}
                    className={`flex items-center rounded-xl px-4 py-3 text-sm font-semibold transition-all duration-200 min-h-[44px] ${
                      active
                        ? "bg-white/20 text-white shadow-md"
                        : "text-blue-50 hover:bg-white/10 hover:text-white"
                    }`}
                  >
                    {link.label}
                  </Link>
                );
              })}
            </div>
          </nav>

          <div className="border-t border-white/10 p-4 space-y-3 shrink-0">
            {!isAuthenticated && !isGuest ? (
              <Link
                to="/signup"
                className="flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-bold text-white transition-all duration-200 bg-green-600 hover:bg-blue-600 min-h-[44px]"
              >
                Sign Up
              </Link>
            ) : null}
          </div>
        </div>
      </div>

      {showScrollTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-32 right-4 z-40 flex h-12 w-12 items-center justify-center rounded-full bg-green-600 text-white shadow-lg hover:bg-green-700 transition-colors md:bottom-24 md:right-6"
          aria-label="Scroll to top"
        >
          <ArrowUp className="h-5 w-5" />
        </button>
      )}
    </>
  );
}
